import React, { HTMLProps, PureComponent, ChangeEvent, RefObject } from 'react'
import Log from '@lib/Log';

/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import { TextInputContainer } from './TextInput.elements';
import { INPUT_TYPE_NUMBER, INPUT_TYPE_TEL } from '@components/Question';
import { formInputErrorStyle } from '@css/styles';

export const FORMATTER_TYPE_AGE = 'age';
export const FORMATTER_TYPE_ZIP = 'zipcode';
export const FORMATTER_TYPE_PHONE_US = 'phone_us';
export const FORMATTER_TYPE_DATE = 'date';
export const FORMATTER_TYPE_CURRENCY = 'currency';

type OwnProps = {
  error: boolean;
  name: string;
  onChange: (name: string, value: string) => void;
  type: string;
  value: string;
  autocomplete?: string;
  formatter?: string;
  placeholder?: string;
  autofill?: string;
  inputRef?: RefObject<HTMLInputElement>;
}

// Overriding onChange prop:
type Props = Override<HTMLProps<HTMLInputElement>, OwnProps>

export class TextInput extends PureComponent<Props> {

  handleFocus = (event: ChangeEvent<HTMLInputElement>) => {
    const { autofill } = this.props
    if (autofill) {
      Log.info(null, '[TextInput handleFocus] should autofill');
    }
  }

  handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      type,
      value,
      onChange,
      formatter,
    } = this.props

    const currentValue = value + '';
    let updatedValue = event.target.value + '';

    switch(formatter) {

      case FORMATTER_TYPE_ZIP: {
        if (updatedValue.length > 5) {
          return;
        }
        break;
      }

      case FORMATTER_TYPE_AGE: {
        if (updatedValue.length > 3 ||
           (updatedValue.charAt(0) !== '1' && updatedValue.length > 2)) {
          return;
        }
        break;
      }

      case FORMATTER_TYPE_PHONE_US: {
        if (updatedValue.length > 14) {
          return;
        }

        // handle add
        if (currentValue.length < updatedValue.length) {
          if (updatedValue.length > 0) {
            let lastChar = parseFloat(updatedValue.charAt(updatedValue.length - 1));
            if (isNaN(lastChar)) {
              updatedValue = currentValue;
            }
          }

          if (updatedValue !== currentValue) {
            if (updatedValue.length === 3 && updatedValue.charAt(0) !== '(') {
              updatedValue = `(${updatedValue}) `;
            } else if (updatedValue.length === 4 && updatedValue.charAt(0) === '(') {
              updatedValue = `${updatedValue}) `;
            } else if (updatedValue.length === 5 && updatedValue.charAt(0) === '(') {
              updatedValue = `${updatedValue.substr(0, 4)}) ${updatedValue.charAt(4)}`;
            } else if (updatedValue.length === 6 && updatedValue.charAt(0) === '(') {
              updatedValue = `${updatedValue.substr(0, 5)} ${updatedValue.charAt(5)}`;
            } else if (updatedValue.length === 9) {
              updatedValue = `${updatedValue}-`;
            } else if (updatedValue.length === 10 && updatedValue.charAt(9) !== '-') {
              updatedValue = `${updatedValue.substr(0, 9)}-${updatedValue.charAt(9)}`;
            }
          }
        }
        // handle delete
        else {
        }

        break;
      }

      case FORMATTER_TYPE_DATE: {
        // Strip any non-numeric (nor /) char inserted by any way.
        updatedValue = updatedValue.replace(/[^\d\/]/g, '')

        const slashCount = (updatedValue.match(/\//g) || []).length

        // Prevent non-digit characters being entered
        if (updatedValue[updatedValue.length - 1] === '/' && slashCount > 2) {
          updatedValue = updatedValue.slice(0, -1)
        }

        // When user is deleting, this prevents immediate re-addition of '/' when it's deleted
        const hasRemovedSlash = value &&
          (value.length === updatedValue.length + 1) &&
          value[updatedValue.length] === '/'
        const endsInTwoSlash = updatedValue.endsWith('//')

        if (hasRemovedSlash || endsInTwoSlash) {
          updatedValue = updatedValue.slice(0, -1)
          break
        }

        if (updatedValue.includes('//')) {
          // Reached a point where it can be hard to recover. Freedom is over.
          updatedValue = ''
        }

        // Removing the "/" if the input starts with it (while not editing).
        if (updatedValue[0] === '/' && value.length < updatedValue.length) {
          updatedValue = updatedValue.substring(1)
        }

        // Adding leading 0's.
        const [month, day, year] = updatedValue.split('/')
        if (!day && !year) {
          if (month.length === 1 && (+month > 1 || updatedValue.length === 2 && updatedValue[updatedValue.length - 1] === '/')) {
            updatedValue = `0${month}/`
          }
        } else if (day && !year) {
          if (day && day.length === 1 && (+day > 3 || updatedValue.length === 5 && updatedValue[updatedValue.length - 1] === '/')) {
            updatedValue = `${month}/0${day}/`
          }
        } else { // editing with month, day and year.
          let newMonth = month
          let newDay = day
          let newYear = year
          if (month.length === 1 && +month > 1) {
            newMonth = `0${month}`
          }
          if (day.length === 1 && +day > 3) {
            newDay = `0${day}`
          }
          updatedValue = `${newMonth}/${newDay}/${newYear}`
        }

        // Append a "/" when months and/or days are filled.
        if (updatedValue.length === 2 || updatedValue.length === 5) {
          updatedValue += '/'
        }

        // Prevent characters being entered after dob is full
        if (updatedValue.length >= 10) {
          let [month, day, year] = updatedValue.split('/')
          if (month.length > 2) {
            // reducing the months to 2 digits.
            month = month.slice(0, 2)
            updatedValue = `${month}/${day}/${year}`
          } else if (day && day.length > 2) {
            // reducing the days to 2 digits.
            day = day.slice(0, 2)
            updatedValue = `${month}/${day}/${year}`
          } else {
            // reducing the years to 4 digits...
            updatedValue = updatedValue.slice(0, 10)
          }
        }
        break
      }

      case FORMATTER_TYPE_CURRENCY: {
        updatedValue = updatedValue.replace(/[^0-9.-]+/g, '')

        const options = {
          maximumFractionDigits: 0,
          currency: 'USD',
          style: 'currency',
          currencyDisplay: 'symbol'
        }
        updatedValue = Number(updatedValue).toLocaleString(undefined, options)
        break
      }
    }

    if (type === INPUT_TYPE_NUMBER && formatter !== FORMATTER_TYPE_PHONE_US) {
      if (isNaN(+updatedValue) || updatedValue === '') {
        updatedValue = '';
      } else {
        updatedValue = parseFloat(updatedValue).toString();
      }
    }

    if (
      type === INPUT_TYPE_TEL &&
      (!formatter || ![FORMATTER_TYPE_DATE, FORMATTER_TYPE_PHONE_US].includes(formatter)) &&
      isNaN(+updatedValue)
    ) {
      updatedValue = '';
    }

    Log.info('TextInput', `updated ${name}: ${event.target.value}`);
    onChange(name, updatedValue);
  }

  render () {
    const {
      name,
      type,
      value,
      error,
      onChange,
      placeholder,
      formatter,
      autocomplete,
      inputRef,
      ...props
    } = this.props

    const hasBoundaries = Number.isInteger(props.min) || Number.isInteger(props.max)

    return (
      <TextInputContainer
        key={name}
        css={error ? [formInputErrorStyle] : null}
        type={type === INPUT_TYPE_NUMBER && !hasBoundaries ? 'tel' : type}
        name={name}
        value={value}
        onChange={this.handleChange}
        autoComplete={autocomplete}
        placeholder={placeholder}
        onFocus={this.handleFocus}
        ref={inputRef}
        {...props}
      />
    );
  }
}
